import React, { useState } from 'react';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { X, Filter } from 'lucide-react';
import { UsageAnalyticsFilter } from '../lib/types';

interface UsageAnalyticsFilterProps {
  filter: UsageAnalyticsFilter;
  onFilterChange: (filter: UsageAnalyticsFilter) => void;
  products?: Array<{ id: string; name: string }>;
  checkoutTimeRanges?: Array<{ id: string; label: string; min: number; max: number }>;
}

export function UsageAnalyticsFilterComponent({ 
  filter, 
  onFilterChange, 
  products = [], 
  checkoutTimeRanges = [] 
}: UsageAnalyticsFilterProps) {
  const [isOpen, setIsOpen] = useState(false);

  const handleProductToggle = (productId: string) => {
    const newProductIds = filter.productIds.includes(productId)
      ? filter.productIds.filter(id => id !== productId)
      : [...filter.productIds, productId];
    
    onFilterChange({
      ...filter,
      productIds: newProductIds
    });
  };

  const handleSelectAllProducts = () => {
    const allProductIds = products.map(p => p.id);
    onFilterChange({
      ...filter,
      productIds: allProductIds
    });
  };

  const handleDeselectAllProducts = () => {
    onFilterChange({
      ...filter,
      productIds: []
    });
  };

  const handleCheckoutTimeChange = (rangeId: string) => {
    onFilterChange({
      ...filter,
      checkoutTimeRangeId: rangeId === 'all' ? undefined : rangeId
    });
  };

  const getSelectedProductNames = () => {
    return filter.productIds.map(id => 
      products.find(p => p.id === id)?.name || id
    );
  };

  const getSelectedCheckoutTimeLabel = () => {
    if (!filter.checkoutTimeRangeId) return 'All Ranges';
    const range = checkoutTimeRanges.find(r => r.id === filter.checkoutTimeRangeId);
    return range?.label || 'All Ranges';
  };

  const hasActiveFilters = filter.productIds.length > 0 || filter.checkoutTimeRangeId;

  return (
    <div className="bg-white p-4 rounded-lg shadow mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Filter Usage Analytics</h3>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-2"
        >
          <Filter className="h-4 w-4" />
          {hasActiveFilters ? 'Filters Active' : 'Add Filters'}
        </Button>
      </div>

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <div className="mb-4 p-3 bg-blue-50 rounded-lg">
          <div className="flex flex-wrap gap-2">
            {filter.productIds.map(productId => {
              const product = products.find(p => p.id === productId);
              return (
                <div key={productId} className="flex items-center gap-1 bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-sm">
                  <span>{product?.name || productId}</span>
                  <button
                    type="button"
                    onClick={() => handleProductToggle(productId)}
                    className="hover:bg-blue-200 rounded-full p-0.5"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              );
            })}
            {filter.checkoutTimeRangeId && (
              <div className="flex items-center gap-1 bg-green-100 text-green-800 px-2 py-1 rounded-full text-sm">
                <span>Checkout: {getSelectedCheckoutTimeLabel()}</span>
                <button
                  type="button"
                  onClick={() => handleCheckoutTimeChange('all')}
                  className="hover:bg-green-200 rounded-full p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Filter Options */}
      {isOpen && (
        <div className="space-y-4">
          {/* Product Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filter by Products
            </label>
            <div className="flex gap-2 mb-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleSelectAllProducts}
                disabled={products.length === 0}
              >
                Select All
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleDeselectAllProducts}
              >
                Clear All
              </Button>
            </div>
            <div className="max-h-32 overflow-y-auto border rounded-lg p-2 space-y-1">
              {products.map(product => (
                <div key={product.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={`product-${product.id}`}
                    checked={filter.productIds.includes(product.id)}
                    onCheckedChange={() => handleProductToggle(product.id)}
                  />
                  <label
                    htmlFor={`product-${product.id}`}
                    className="text-sm text-gray-700 cursor-pointer"
                  >
                    {product.name}
                  </label>
                </div>
              ))}
              {products.length === 0 && (
                <p className="text-sm text-gray-500">No products available</p>
              )}
            </div>
          </div>

          {/* Checkout Time Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filter by Checkout Time Range
            </label>
            <Select value={filter.checkoutTimeRangeId || 'all'} onValueChange={handleCheckoutTimeChange}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select checkout time range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Ranges</SelectItem>
                {checkoutTimeRanges.map(range => (
                  <SelectItem key={range.id} value={range.id}>
                    {range.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}
    </div>
  );
}

